<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from "vue";
import { onClickOutside } from "@vueuse/core";

// Props
const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    default: "",
  },
  icon: {
    type: String,
    default: "",
  },
  width: {
    type: String,
    default: "max-w-2xl",
  },
  classBody: {
    type: String,
    default: "",
  },
  persistent: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue"]);

const modalOpen = ref(false);

watch(
  () => props.modelValue,
  (newVal) => {
    modalOpen.value = newVal;
    if (newVal) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
  },
  { immediate: true }
);

const closeModal = () => {
  if (!props.persistent) {
    modalOpen.value = false;
    emit("update:modelValue", false);
  }
};

const handleEscapeKey = (e: KeyboardEvent) => {
  if (e.key === "Escape") closeModal();
};

onMounted(() => {
  document.addEventListener("keydown", handleEscapeKey);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleEscapeKey);
});

const modalRef = ref(null);
onClickOutside(modalRef, () => closeModal(), {
  ignore: ['.p-select-overlay', '.p-datepicker', '.p-autocomplete-overlay', '.p-multiselect-overlay'],
});
</script>

<template>
  <!-- Modal Backdrop -->
  <transition name="fade">
    <div
      v-if="modalOpen"
      class="modal fixed top-0 left-0 z-99999 flex h-full w-full items-center justify-center bg-gray-400/50 backdrop-blur-[25px] p-4"
    >
      <!-- Modal Content -->
      <div
        ref="modalRef"
        :class="[`relative flex flex-col w-full ${width} max-h-[calc(100vh-40px)] rounded-lg bg-white dark:bg-gray-800 py-6 shadow-lg dark:bg-boxdark`]"
      >
        <!-- Modal Header -->
        <div v-if="title || icon" class="modal-header flex items-center justify-between border-b border-gray-300 dark:border-gray-700 mx-6 pb-4">
          <h4 class="text-xl text-black dark:text-white flex items-center gap-2">
            <i v-if="icon" class="material-icons">{{ icon }}</i>
            {{ title }}
          </h4>

          <!-- Close Button -->
          <button
            @click="closeModal"
            class="flex h-7 w-7 items-center justify-center rounded-full text-gray-500 hover:bg-gray-200 dark:text-white dark:hover:bg-gray-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="fill-current" viewBox="0 0 384 512" width="20" height="20">
              <path d="M324.5 411.1c6.2 6.2 16.4 6.2 22.6 0s6.2-16.4 0-22.6L214.6 256 347.1 123.5c6.2-6.2 6.2-16.4 0-22.6s-16.4-6.2-22.6 0L192 233.4 59.6 100.9c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6L169.4 256 36.9 388.5c-6.2 6.2-6.2 16.4 0 22.6s16.4 6.2 22.6 0L192 278.6 324.5 411.1z"/>
            </svg>
          </button>
        </div>

        <!-- Modal Body (scrollable) -->
        <div
          :class="[
            'modal-body flex-1 overflow-auto px-6 py-4',
            classBody,
          ]"
        >
          <slot></slot>
        </div>

        <!-- Loading overlay — sibling of body, NOT inside it.
             Covers the entire modal card (header + body + footer) via
             absolute positioning on the parent which already has `relative`. -->
        <div
          v-if="loading"
          class="absolute bottom-[85px] inset-0 rounded-lg bg-white/80 dark:bg-gray-800/80 z-10 flex items-center justify-center"
          style="backdrop-filter: blur(2px);"
        >
          <div class="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent"></div>
        </div>

        <!-- Modal Footer -->
        <div v-if="$slots.footer" class="modal-footer flex justify-end gap-2 border-t border-gray-300 dark:border-gray-700 mx-6 pt-4">
          <slot name="footer"></slot>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease-in-out;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
